/**
 * HERO RIBBONS BACKGROUND
 * ======================
 *
 * Canvas-based animated ribbon background used in the hero section.
 * Inspired by classic "wave / ribbon" demos that use Bezier curves driven
 * by evolving phase angles (no GSAP, no dependencies).
 *
 * CORE IDEA
 * ---------
 * - Each ribbon is NOT a single shape.
 * - A ribbon is a *trail* of Bezier segments.
 * - Each segment is defined by 4 phase angles that evolve over time.
 * - Drawing many slightly-offset, translucent segments creates the
 *   illusion of a smooth, flowing ribbon.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * This file is responsible ONLY for:
 * - ribbon geometry
 * - ribbon motion
 * - visual styling of ribbons (thickness, color, alpha, highlight)
 *
 * It does NOT:
 * - create or size the canvas
 * - manage resize / visibility / performance lifecycle
 * - know anything about the page or DOM
 *
 * That separation keeps this effect reusable and easy to tune.
 *
 *
 * KEY CONCEPTS
 * ------------
 *
 * 1) RIBBONS
 *    - `count`: how many independent ribbons exist
 *    - Each ribbon has its own phase angles, speed, thickness, and alpha
 *
 * 2) TRAIL
 *    - `trail`: number of Bezier segments stored per ribbon
 *    - Higher = smoother + longer ribbon, but more expensive
 *
 * 3) STEP (PERFORMANCE KNOB)
 *    - `step`: draw every Nth segment
 *    - Example: step = 2 draws half the segments
 *    - This is the main performance lever
 *
 * 4) THICKNESS & ALPHA
 *    - thicknessMin / thicknessMax control visual weight
 *    - baseAlpha controls how "backgroundy" the ribbons feel
 *    - thinner + lower alpha = more subtle, reference-like look
 *
 * 5) TWIST ILLUSION (NO PINCHING)
 *    - Ribbons do NOT scale width to simulate twist
 *    - Instead, a highlight ("sheen") slides side-to-side across the ribbon
 *    - This creates a convincing twist/roll illusion without artifacts
 *
 * 6) RADIUS / SCALE
 *    - `radiusScale` controls how far Bezier endpoints extend
 *    - Values > 1 push endpoints offscreen so ribbon ends never appear
 *
 *
 * IMPORTANT TUNING GUIDELINES
 * --------------------------
 *
 * - If FPS drops:
 *   → increase `step`
 *   → reduce `trail`
 *   → reduce `count`
 *
 * - If ribbons feel too dominant:
 *   → reduce thicknessMax
 *   → reduce baseAlpha
 *
 * - If ribbon ends are visible:
 *   → increase radiusScale (e.g. 1.25–1.4)
 *
 * - If effect feels too wild:
 *   → reduce amplitude
 *   → reduce speedMin / speedMax
 *
 *
 * ACCESSIBILITY
 * -------------
 * - Respects prefers-reduced-motion
 * - In reduced motion mode, ribbons render a single still frame
 *
 *
 * EXTENSIBILITY
 * -------------
 * This system can support:
 * - additional ribbon layers (foreground / background)
 * - different color themes
 * - alternate trail styles (dots, dashed strokes, gradients)
 *
 * Without changing heroWaves.js.
 */

const TAU = Math.PI * 2;

function rand(a, b) {
  if (b === undefined) return Math.random() * a;
  return a + Math.random() * (b - a);
}
function randSign() {
  return Math.random() > 0.5 ? 1 : -1;
}

export function createHeroRibbons(ctx, width, height, opts = {}) {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const options = {
    // core look
    count: 3, // number of ribbons
    trail: 120, // segments per ribbon
    step: 2, // draw every Nth segment for perf (1 = all)
    highlightEvery: 2, // draw highlight every Nth segment
    rotationDeg: 25, // diagonal flow
    amplitude: 0.65, // bend amount (0.4–0.9)
    radiusScale: 1.25, // reach toward corners

    // motion
    speedMin: 0.0035,
    speedMax: 0.0075,

    // ribbon styling
    baseAlpha: 0.05,
    highlightAlpha: 0.018,
    thicknessMin: 12,
    thicknessMax: 28,
    highlightWidthRatio: 0.22, // inner highlight width = thickness * ratio

    // "twist" (sheen slide) illusion
    twistSheenAmount: 0.12, // fraction of thickness used for sheen offset
    twistSheenTimeScale: 6.0, // how fast the sheen "walks" along the ribbon

    // colors
    baseColor: [70, 130, 180],
    highlightColor: [120, 200, 220],

    // compositing
    composite: "lighter",

    ...opts,
  };

  const rotation = (options.rotationDeg * Math.PI) / 180;
  const cx = width / 2;
  const cy = height / 2;

  // Use a “radius” based on diagonal, like the snippet
  const radius = Math.sqrt(width * width + height * height) * 0.5 * options.radiusScale;
  const radius3 = radius / 3;

  class Ribbon {
    constructor() {
      this.segments = [];

      // 4 phase angles + 4 speeds (matches the reference feel)
      this.angle = [rand(TAU), rand(TAU), rand(TAU), rand(TAU)];
      this.speed = [
        rand(options.speedMin, options.speedMax) * randSign(),
        rand(options.speedMin, options.speedMax) * randSign(),
        rand(options.speedMin, options.speedMax) * randSign(),
        rand(options.speedMin, options.speedMax) * randSign(),
      ];

      this.thickness = rand(options.thicknessMin, options.thicknessMax);
      this.alpha = options.baseAlpha * rand(0.85, 1.15);
      this.hAlpha = options.highlightAlpha * rand(0.85, 1.15);

      this.amp = options.amplitude * rand(0.9, 1.1);

      // sheen slide phase to fake "twist"
      this.sheenPhase = rand(TAU);
      this.sheenSpeed = rand(0.6, 1.2); // rad/sec-ish
    }

    update(dt) {
      const a = this.angle;
      const s = this.speed;

      // dt-scaled so speed stays consistent across refresh rates
      const k = dt * 60;

      a[0] += s[0] * k;
      a[1] += s[1] * k;
      a[2] += s[2] * k;
      a[3] += s[3] * k;

      this.sheenPhase += this.sheenSpeed * dt;

      // store sin’d angles like the reference does
      this.segments.push({
        a0: Math.sin(a[0]),
        a1: Math.sin(a[1]),
        a2: Math.sin(a[2]),
        a3: Math.sin(a[3]),
      });

      if (this.segments.length > options.trail) this.segments.shift();
    }

    draw(now) {
      const [br, bg, bb] = options.baseColor;
      const [hr, hg, hb] = options.highlightColor;

      const segs = this.segments;
      const denom = segs.length - 1 || 1;

      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // draw tail -> head
      for (let i = 0; i < segs.length; i += options.step) {
        const seg = segs[i];

        const t = i / denom;
        const fade = 0.18 + 0.82 * t; // tail slightly dimmer

        // Map segment “angles” to endpoints + controls
        const x1 = cx - radius * Math.cos(seg.a0 * this.amp + rotation);
        const y1 = cy - radius * Math.sin(seg.a0 * this.amp + rotation);

        const x2 = cx + radius * Math.cos(seg.a3 * this.amp + rotation);
        const y2 = cy + radius * Math.sin(seg.a3 * this.amp + rotation);

        const cpx1 = cx - radius3 * Math.cos(seg.a1 * this.amp * 2);
        const cpy1 = cy - radius3 * Math.sin(seg.a1 * this.amp * 2);

        const cpx2 = cx + radius3 * Math.cos(seg.a2 * this.amp * 2);
        const cpy2 = cy + radius3 * Math.sin(seg.a2 * this.amp * 2);

        // Base ribbon stroke
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, x2, y2);

        ctx.lineWidth = this.thickness;
        ctx.strokeStyle = `rgba(${br},${bg},${bb},${this.alpha * fade})`;
        ctx.stroke();

        // Highlight stroke (sheen)
        if (i % options.highlightEvery !== 0) continue;

        // Approximate a perpendicular direction for a subtle offset.
        // This creates the "twist" illusion: sheen slides side-to-side across the ribbon.
        const dx = x2 - x1;
        const dy = y2 - y1;
        const invLen = 1 / (Math.hypot(dx, dy) || 1);
        const nx = -dy * invLen;
        const ny = dx * invLen;

        const slide =
          Math.sin(this.sheenPhase + t * options.twistSheenTimeScale) * (this.thickness * options.twistSheenAmount);

        ctx.beginPath();
        ctx.moveTo(x1 + nx * slide, y1 + ny * slide);
        ctx.bezierCurveTo(
          cpx1 + nx * slide,
          cpy1 + ny * slide,
          cpx2 + nx * slide,
          cpy2 + ny * slide,
          x2 + nx * slide,
          y2 + ny * slide,
        );

        ctx.lineWidth = Math.max(1, this.thickness * options.highlightWidthRatio);
        ctx.strokeStyle = `rgba(${hr},${hg},${hb},${this.hAlpha * fade})`;
        ctx.stroke();
      }
    }
  }

  const ribbons = Array.from({ length: options.count }, () => new Ribbon());

  return {
    draw(now, dt) {
      ctx.save();
      ctx.globalCompositeOperation = options.composite;

      if (!prefersReducedMotion) {
        ribbons.forEach((r) => r.update(dt));
      }

      ribbons.forEach((r) => r.draw(now));

      ctx.restore();
    },
  };
}
